import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, Loading } from "@/components/shared";
import GoogleSheetsConnector from "@/components/GoogleSheetsConnector";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, Layers, XCircle } from "lucide-react";

const STATUS = {
  connected: { tone: "bg-emerald-100 text-emerald-800", label: "Connected", icon: CheckCircle2 },
  setup_required: { tone: "bg-amber-100 text-amber-800", label: "Setup required", icon: AlertTriangle },
  not_available: { tone: "bg-slate-100 text-slate-600", label: "Not in this version", icon: XCircle },
};

export default function Integrations() {
  const { data, isLoading } = useQuery({ queryKey: ["integrations"], queryFn: async () => (await api.get("/integrations")).data });
  if (isLoading) return <Loading />;

  return (
    <div>
      <PageHeader title="Integrations" subtitle="We never fake a live connection. Each shows exactly what's needed next." testid="integrations-header" />
      <div className="mb-4"><GoogleSheetsConnector /></div>
      <div className="grid lg:grid-cols-2 gap-4">
        {data.filter((it) => it.id !== "google_sheets").map((it) => {
          const st = STATUS[it.status] || STATUS.setup_required;
          const Icon = st.icon;
          return (
            <div key={it.id} className="rounded-xl border bg-white dark:bg-slate-800 p-5" data-testid={`integration-${it.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-brand-blue grid place-items-center"><Layers className="w-5 h-5" /></div>
                  <div><div className="font-head font-bold">{it.name}</div><div className="text-sm text-slate-500">{it.description}</div></div>
                </div>
                <Badge className={`${st.tone} gap-1`}><Icon className="w-3 h-3" /> {st.label}</Badge>
              </div>

              <div className="mt-4 text-sm">
                <div className="font-semibold text-xs uppercase text-slate-400 mb-1">Requirements</div>
                <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-0.5">{it.required.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </div>
              <div className="mt-3 text-sm">
                <div className="font-semibold text-xs uppercase text-slate-400 mb-1">Next steps</div>
                <ol className="list-decimal list-inside text-slate-600 dark:text-slate-300 space-y-0.5">{it.setup_steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
              </div>
              {it.notes && <p className="mt-3 text-xs text-slate-500 bg-slate-50 dark:bg-slate-900 rounded-lg p-2">{it.notes}</p>}
              {it.last_error && <p className="mt-2 text-xs text-red-600">Last error: {it.last_error}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
